/* eslint-disable @typescript-eslint/no-explicit-any */
import CommonTab from './tab/tab';
import {
  WrapperContainer,
  getWrapperProps
} from '@bayada/shared/ui-components';
import { CmsImageProps } from '@bayada/interfaces';
export type CmsTabProps = {
  tabGroupTitle?: string;
  tabItems?: TabItemProps[];
  position?: string;
  tabStyle?: 'boxed' | 'regular';
  sys?: any;
  backgroundColor?: string;
};

export type TabItemProps = {
  tabLabel: string;
  icon: CmsImageProps | null;
  iconPosition: string;
  content: any;
  contentPosition: string;
  sys?: any;
};

export type CmsTabItemProps = {
  tabItems?: TabItemProps[];
  isWrapTheme?: boolean;
  isCentered?: boolean;
};
/* The `export const CmsTab` function is a React component that renders a tab component based on the props passed to it. Here's a breakdown of what the function does: */
/**
 * The code defines TypeScript React components for rendering tab components with customizable styles
 * and content.
 * @property {string} tabGroupTitle - The `tabGroupTitle` property is a string that represents the
 * title of the tab group. It is used to provide a title for the group of tabs that will be displayed
 * in the UI.
 * @property {TabItemProps[]} tabItems - The `tabItems` property in the `CmsTabProps` type represents
 * an array of `TabItemProps` objects. Each `TabItemProps` object contains information about a single
 * tab item, including the tab label, icon, icon position, content, content position, and any
 * additional system
 * @property {string} position - The `position` property in the `CmsTabProps` type is a string that
 * specifies the position of the tab component. It is used to control the layout or alignment of the
 * tab component within its container. The value of this property can be customized based on the design
 * requirements of the application.
 * @property {'boxed' | 'regular'} tabStyle - The `tabStyle` property in the `CmsTabProps` type
 * specifies the style of the tab component. It can have two possible values:
 * @property {any} sys - The `sys` property in the `CmsTabProps` and `TabItemProps` types is defined as
 * `any`. This means that the `sys` property can hold any type of value without strict type checking.
 * It is often used when the structure or type of the property is not known
 */

export const CmsTab = (props: CmsTabProps) => {
  /* This return value of the `getWrapperProps` as true or false. Based on the value, the component will render inside the `WrapperContainer` */
  const { wrapperContainer } = getWrapperProps(props);

  const bg =
    props?.backgroundColor === 'light grey'
      ? 'bg-ba-gray-75'
      : props?.backgroundColor === 'light blue'
        ? 'bg-ba-blue-150'
        : props?.backgroundColor === 'white'
          ? 'bg-white'
          : 'bg-transparent';

  return (
    <WrapperContainer
      wrapper={wrapperContainer}
      containerCoverClass={`flex flex-col ${bg}`}
      className={`relative py-8 md:py-12`}
    >
      {props?.tabStyle === 'regular' ? (
        <div
          className="block"
          role="region"
          aria-label={props?.tabGroupTitle || 'Tab Content'}
        >
          <div className="flex flex-col justify-start">
            {props?.tabGroupTitle && (
              <h2 className="t-33-46 text-primary-black mb-10 font-frutiger font-bold">
                {props?.tabGroupTitle}
              </h2>
            )}
            <CommonTab
              isCentered={false}
              isWrapTheme={true}
              tabItems={props?.tabItems}
            />
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col"
          role="region"
          aria-label={props?.tabGroupTitle || 'Tab Content'}
        >
          <div className="flex flex-col">
            <CommonTab
              isCentered={true}
              isWrapTheme={false}
              tabItems={props?.tabItems}
            />
          </div>
        </div>
      )}
    </WrapperContainer>
  );
};

export default CmsTab;
